// app/javascript/controllers/auto_save_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["form", "status"]
    static values = {
        url: String,
        delay: { type: Number, default: 2000 }
    }

    connect() {
        this.debounceTimer = null
        this.lastSavedContent = this.getFormData()
        this.setupListeners()
    }

    disconnect() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
    }

    setupListeners() {
        // Listen to Trix/Lexxy editor changes
        this.element.addEventListener('trix-change', this.scheduleAutoSave.bind(this))
        this.element.addEventListener('lexxy:change', this.scheduleAutoSave.bind(this))

        // Listen to regular input changes
        this.element.addEventListener('input', this.scheduleAutoSave.bind(this))
    }

    scheduleAutoSave() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }

        this.updateStatus('unsaved')

        this.debounceTimer = setTimeout(() => {
            this.performAutoSave()
        }, this.delayValue)
    }

    async performAutoSave() {
        const currentContent = this.getFormData()

        // Check if content actually changed
        if (this.contentEquals(currentContent, this.lastSavedContent)) {
            return
        }

        this.updateStatus('saving')

        try {
            const formData = new FormData(this.formTarget || this.element.querySelector('form'))

            const response = await fetch(this.urlValue || this.formTarget?.action, {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': this.csrfToken,
                    'Accept': 'application/json'
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Auto-save failed')
            }

            this.lastSavedContent = currentContent
            this.updateStatus('saved')

        } catch (error) {
            console.error('Auto-save error:', error)
            this.updateStatus('error')
        }
    }

    updateStatus(status) {
        if (!this.hasStatusTarget) return

        const statusElement = this.statusTarget
        statusElement.classList.remove('text-primary-600', 'text-success-600', 'text-danger-600', 'text-warning-600')

        switch (status) {
            case 'saving':
                statusElement.textContent = statusElement.dataset.savingText || 'Saving...'
                statusElement.classList.add('text-primary-600')
                break
            case 'saved':
                statusElement.textContent = statusElement.dataset.savedText || 'Saved'
                statusElement.classList.add('text-success-600')
                // Clear after a moment
                setTimeout(() => {
                    if (statusElement.textContent === (statusElement.dataset.savedText || 'Saved')) {
                        statusElement.textContent = ''
                    }
                }, 3000)
                break
            case 'error':
                statusElement.textContent = statusElement.dataset.errorText || 'Save failed'
                statusElement.classList.add('text-danger-600')
                break
            case 'unsaved':
                statusElement.textContent = ''
                break
        }
    }

    getFormData() {
        const form = this.formTarget || this.element.querySelector('form')
        if (!form) return {}

        const data = {}
        const formData = new FormData(form)
        for (const [key, value] of formData.entries()) {
            data[key] = value
        }
        return data
    }

    contentEquals(a, b) {
        return JSON.stringify(a) === JSON.stringify(b)
    }

    get csrfToken() {
        return document.querySelector("[name='csrf-token']")?.content || ''
    }
}
